import {
  type MaybeRefOrGetter,
  computed,
  onUnmounted,
  ref,
  toValue,
  watch,
} from 'vue'

import { useMounted } from '@vueuse/core'

// visualViewport.height is never bit-identical to the layout viewport (100dvh):
// scrollbars, URL-bar collapse animations, etc. all shave a few px off it even
// with no keyboard open. Only treat it as "the keyboard opened" past this
// margin — comfortably above that noise, comfortably below the smallest real
// on-screen keyboard (~200px+) — so ordinary loads never leave `100dvh`, and
// the pixel value only kicks in for the actual keyboard-avoidance case that
// needs it.
const KEYBOARD_INSET_THRESHOLD_PX = 100

// Cache the initial layout viewport height on first attach. On iOS WKWebView,
// window.innerHeight shrinks along with visualViewport.height when the keyboard
// opens, so their difference is always ~0. We need the *original* height to
// detect the keyboard inset.
let initialInnerHeight: number | null = null

/**
 * Tracks the visual viewport so a full-screen layout can stay glued to the
 * area that is actually visible above the on-screen keyboard.
 *
 * Why this exists: on iOS Safari / WKWebView the *layout* viewport does NOT
 * shrink when the software keyboard opens — `100vh`, `100svh`, `100lvh` and
 * even `100dvh` all keep reporting the full screen height (dvh only accounts
 * for collapsing browser chrome, not the keyboard). Only the *visual* viewport
 * shrinks, and `position: fixed` stays pinned to the unchanged layout viewport,
 * which is what makes a fixed input row end up hidden behind the keyboard.
 *
 * Binding the chat container's height to `visualViewport.height` (and offsetting
 * it by `visualViewport.offsetTop`, since iOS scrolls the layout viewport when
 * the keyboard appears) keeps the input above the keyboard on every platform.
 *
 * Falls back to `100dvh` before mount, when the API is unavailable (SSR, older
 * engines), and whenever the keyboard isn't actually eating space — so the
 * common case never trades a stable, native `dvh` for a JS-measured pixel
 * value that's merely close to it, which is what caused the input bar to
 * visibly shift on every load, keyboard or not.
 *
 * @param enabled - Whether to track the viewport, e.g. only while a host that
 *   outlives its open/closed state (mounted for the layout's lifetime rather
 *   than the route's) actually has its panel open. Defaults to always-on,
 *   matching a host that mounts/unmounts with the feature itself. Toggling
 *   this off detaches listeners and resets to the `100dvh` fallback; toggling
 *   it back on re-syncs immediately rather than waiting for the next resize.
 */
export function useVisualViewport(enabled: MaybeRefOrGetter<boolean> = true) {
  // Height of the area not covered by the keyboard, or null when the keyboard
  // isn't open (including before mount / when the VisualViewport API is
  // unavailable), in which case the caller should fall back to `100dvh`.
  const height = ref<number | null>(null)
  const offsetTop = ref(0)

  let viewport: VisualViewport | null = null
  let rafId: number | null = null

  function sync() {
    if (!viewport) return
    const baseline = initialInnerHeight ?? window.innerHeight
    const keyboardInset = baseline - viewport.height
    height.value =
      keyboardInset > KEYBOARD_INSET_THRESHOLD_PX ? viewport.height : null
    offsetTop.value = viewport.offsetTop
  }

  function scheduleSync() {
    if (rafId !== null) return
    rafId = window.requestAnimationFrame(() => {
      rafId = null
      sync()
    })
  }

  function attach() {
    viewport = window.visualViewport ?? null
    if (!viewport) return
    initialInnerHeight = window.innerHeight
    sync()
    viewport.addEventListener('resize', scheduleSync)
    viewport.addEventListener('scroll', scheduleSync)
  }

  function detach() {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId)
      rafId = null
    }
    if (!viewport) return
    viewport.removeEventListener('resize', scheduleSync)
    viewport.removeEventListener('scroll', scheduleSync)
    viewport = null
    initialInnerHeight = null
    height.value = null
    offsetTop.value = 0
  }

  // Gated on `isMounted` (rather than only calling attach()/detach() from
  // onMounted) so toggling `enabled` after mount — e.g. a host that stays
  // mounted for the layout's lifetime and flips this on/off as its panel
  // opens/closes — re-attaches or tears down immediately. The `immediate`
  // run happens during setup, including on the server, where `isMounted` is
  // still false, so attach()/detach() never touch `window` before mount.
  const isMounted = useMounted()

  onUnmounted(detach)

  watch(
    [() => toValue(enabled), isMounted],
    ([isEnabled, mounted]) => {
      if (mounted && isEnabled) {
        attach()
      } else {
        detach()
      }
    },
    // sync: attach()/detach() run the instant `isMounted`/`enabled` change
    // (still inside useMounted's own onMounted for the initial attach),
    // instead of a tick later — matching the old onMounted-only behaviour.
    { immediate: true, flush: 'sync' },
  )

  // Style for the chat root: a definite pixel height keeps the inner flex
  // column (and its scroll area) sized to the visible region, while the
  // translate compensates for iOS shifting the layout viewport up.
  const style = computed(() => ({
    height: height.value === null ? '100dvh' : `${height.value}px`,
    transform: `translateY(${offsetTop.value}px)`,
  }))

  return { height, offsetTop, style }
}
